import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import {
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faEllipsisVertical,
  faPrint,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

import mailroomService from "api/mailroomService";
import { IPackage } from "dto/IPackage";
import { ItemKind } from "dto/IKindItems";
import {
  IIncomingRegisterItem,
  IIncomingRegisterRequest,
  IInternalRegisterItem,
  IInternalRegisterRequest,
  IOutgoingRegisterItem,
  IOutgoingRegisterRequest,
} from "dto/Mailroom/IMailroomRegister";
import PeriodPicker from "components/Registry/PeriodPicker";
import SearchEmptyImg from "components/SearchEmptyImg";
import ShowPackageModal from "components/ShowPackageModal";
import ParcelPreviewModal from "modules/biuroPodawczeModule/ParcelPreviewModal";
import { plGridLocales } from "utils/muiGridLocales";
import { ENotificationType, notification } from "utils/notification";
import { IPrintRegisterColumn, printRegister } from "utils/printRegister";
import useGridRowSelection from "hooks/useGridRowSelection";

import { MAILROOM_REGISTER_TITLES, MailroomRegisterMode } from "./types";
import styles from "./styles.module.scss";

/**
 * Rejestry biura podawczego — port modułu „Rejestry" z Magistratu
 * (Kwantum.Documents.Register.UI: ParcelFilter / ParcelFeeFilter / InternalDocumentFilter /
 * DeletedParcelsFilter + „Drukuj listę" i „Drukuj listę doręczeń").
 *
 * Tryby:
 *  - incoming  — przesyłki przychodzące (okres po dacie wpływu, forma doręczenia, rodzaj),
 *  - outgoing  — przesyłki wychodzące (okres po dacie nadania, forma doręczenia),
 *  - internal  — dokumenty wewnętrzne (zatwierdzone bez pism wychodzących — SignedInternal),
 *  - fees      — rozliczenia opłat pocztowych (rok/miesiąc, kolumna Opłata, „Suma opłat"),
 *  - archive   — archiwum (zamknięte przychodzące/wychodzące, przełącznik kierunku).
 * Dane z dedykowanych endpointów /mailroom/register/* (DocumentsView nie filtruje po dacie).
 */

interface Props {
  mode: MailroomRegisterMode;
  searchText: string;
  setSubtitle: (s: string) => void;
}

type Direction = "incoming" | "outgoing";
type PopupPos = { top: number; left: number } | null;

/** Wiersz grida — pozycja dowolnego z trzech rejestrów (pola wspólne: Id, Title, CaseId). */
type RegisterRow = (IIncomingRegisterItem | IOutgoingRegisterItem | IInternalRegisterItem) & {
  id: string;
};

const PAGE_SIZE = 50;
const NOW = new Date();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => NOW.getFullYear() - i);
const MONTHS = [
  "styczeń", "luty", "marzec", "kwiecień", "maj", "czerwiec",
  "lipiec", "sierpień", "wrzesień", "październik", "listopad", "grudzień",
];

const pad2 = (n: number) => String(n).padStart(2, "0");
const toISO = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fmtDate = (v?: string | null) => (v ? moment(v).format("DD.MM.YYYY") : "");
const plDate = (iso: string) => (iso ? moment(iso).format("DD.MM.YYYY") : "");
const fmtPln = (v?: number | null) =>
  v == null
    ? ""
    : new Intl.NumberFormat("pl-PL", {
        style: "currency",
        currency: "PLN",
      }).format(v);

const CustomNoRowsOverlay = () => (
  <div className={styles.empty}>
    <SearchEmptyImg />
    <div>Brak pozycji w rejestrze</div>
  </div>
);

const MailroomRegister = ({ mode, searchText, setSubtitle }: Props) => {
  const navigate = useNavigate();
  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();

  // ── Filtry ──────────────────────────────────────────────────────────────
  const [year, setYear] = useState<number>(NOW.getFullYear());
  const [from, setFrom] = useState<string>(toISO(new Date(NOW.getFullYear(), 0, 1)));
  const [to, setTo] = useState<string>(toISO(NOW));
  const [preset, setPreset] = useState<string | null>("biezacyRok");
  const [periodPopupPos, setPeriodPopupPos] = useState<PopupPos>(null);

  const [deliveryKindId, setDeliveryKindId] = useState<number | "">("");
  const [attachmentTypeId, setAttachmentTypeId] = useState<number | "">("");
  const [feeYears, setFeeYears] = useState<number[]>([NOW.getFullYear()]);
  const [feeMonths, setFeeMonths] = useState<number[]>([]);
  const [direction, setDirection] = useState<Direction>("incoming");

  // Strona jest ważna tylko dla sygnatury filtra, przy której ją ustawiono — zmiana
  // dowolnego filtra (także propsa searchText) zeruje ją w TYM SAMYM renderze, bez
  // pośredniego żądania ze starym Skip i nowym filtrem.
  const filterKey = JSON.stringify([mode, from, to, deliveryKindId, attachmentTypeId, feeYears, feeMonths, direction, searchText]);
  const [pageState, setPageState] = useState({ key: filterKey, page: 0 });
  const page = pageState.key === filterKey ? pageState.page : 0;
  const setPage = (p: number) => setPageState({ key: filterKey, page: p });

  const { data: kindsData } = mailroomService.useGetDeliveryKinds();
  const { data: typesData } = mailroomService.useGetAttachmentTypes();
  const deliveryKinds = kindsData?.data ?? [];
  const attachmentTypes = typesData?.data ?? [];

  // Archiwum zawsze startuje od przychodzących.
  useEffect(() => {
    setDirection("incoming");
  }, [mode]);

  const effectiveDirection: Direction =
    mode === "incoming" ? "incoming"
    : mode === "outgoing" || mode === "fees" ? "outgoing"
    : mode === "archive" ? direction
    : "incoming";

  const usesPeriod = mode !== "fees";
  const showsIncoming = (mode === "incoming" || mode === "archive") && effectiveDirection === "incoming";
  const showsOutgoing = (mode === "outgoing" || mode === "fees" || mode === "archive") && effectiveDirection === "outgoing";
  const showsInternal = mode === "internal";

  const baseRequest = {
    From: usesPeriod && from ? from : null,
    To: usesPeriod && to ? to : null,
    Text: searchText || null,
    Skip: page * PAGE_SIZE,
    Take: PAGE_SIZE,
  };

  const incomingRequest: IIncomingRegisterRequest = {
    ...baseRequest,
    DeliveryKindId: deliveryKindId === "" ? null : deliveryKindId,
    AttachmentTypeId: attachmentTypeId === "" ? null : attachmentTypeId,
    Archived: mode === "archive",
  };
  const outgoingRequest: IOutgoingRegisterRequest = {
    ...baseRequest,
    DeliveryKindId: deliveryKindId === "" ? null : deliveryKindId,
    Years: mode === "fees" ? feeYears : null,
    Months: mode === "fees" ? feeMonths : null,
    FeesOnly: mode === "fees",
    Archived: mode === "archive",
  };
  const internalRequest: IInternalRegisterRequest = { ...baseRequest };

  const incomingQ = mailroomService.useGetIncomingRegister(incomingRequest, showsIncoming);
  const outgoingQ = mailroomService.useGetOutgoingRegister(outgoingRequest, showsOutgoing);
  const internalQ = mailroomService.useGetInternalRegister(internalRequest, showsInternal);

  const isLoading = showsIncoming ? incomingQ.isLoading
    : showsOutgoing ? outgoingQ.isLoading
    : internalQ.isLoading;

  const total = showsIncoming ? incomingQ.data?.data?.Total ?? 0
    : showsOutgoing ? outgoingQ.data?.data?.Total ?? 0
    : internalQ.data?.data?.Total ?? 0;

  const isTeamView = showsIncoming ? incomingQ.data?.data?.IsTeamView
    : showsOutgoing ? outgoingQ.data?.data?.IsTeamView
    : internalQ.data?.data?.IsTeamView;

  const totalFee = outgoingQ.data?.data?.TotalFee ?? 0;
  const feeByKind = outgoingQ.data?.data?.FeeByKind ?? [];

  const rows = useMemo<RegisterRow[]>(() => {
    const items: Array<IIncomingRegisterItem | IOutgoingRegisterItem | IInternalRegisterItem> =
      showsIncoming ? incomingQ.data?.data?.Items ?? []
      : showsOutgoing ? outgoingQ.data?.data?.Items ?? []
      : internalQ.data?.data?.Items ?? [];
    return items.map((it) => ({ id: it.Id, ...it }));
  }, [showsIncoming, showsOutgoing, incomingQ.data, outgoingQ.data, internalQ.data]);
  rowsRef.current = rows;

  // Podtytuł: rodzaj + okres (jak Label PeriodFilter w Magistracie).
  useEffect(() => {
    const period = mode === "fees"
      ? `${feeYears.length ? feeYears.join(", ") : "wszystkie lata"}${feeMonths.length ? " · " + feeMonths.map((m) => MONTHS[m - 1]).join(", ") : ""}`
      : from || to ? `${plDate(from)} – ${plDate(to)}` : "cały okres";
    const dir = mode === "archive" ? (direction === "incoming" ? "przychodzące" : "wychodzące") + " · " : "";
    setSubtitle(`${dir}${period}${isTeamView === false ? " · tylko moje" : ""}`);
  }, [mode, from, to, feeYears, feeMonths, direction, isTeamView, setSubtitle]);

  // ── Podgląd / akcje wiersza ─────────────────────────────────────────────
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: RegisterRow } | null>(null);
  const [preview, setPreview] = useState<{ id: string; kind: ItemKind; title?: string | null } | null>(null);

  const openPreview = (row: RegisterRow | undefined) => {
    if (!row) return;
    if (showsInternal) {
      // Dokument z edytora nie jest przesyłką — `Package`/ShowPackageModal go nie renderuje.
      // Otwieramy podpisany PDF tak jak „otwórz w nowej karcie" w PreviewModal (/document?pdf=).
      // Podgląd tylko podpisanego PDF (DOCX źródłowy nie wyrenderuje się w PDFViewer).
      const doc = row as IInternalRegisterItem & { id: string };
      if (!doc.SignedUri) {
        notification({
          type: ENotificationType.INFO,
          title: "Brak podpisanej wersji",
          text: "Dokument nie ma zapisanego podpisanego PDF — otwórz go z Dysku teczki.",
        });
        return;
      }
      // Nazwa bez ":" — /document dzieli parametr po dwukropkach.
      const name = `${(doc.Title ?? "dokument").replace(/[:/\\]/g, "_")}.pdf`;
      window.open(
        `${import.meta.env.VITE_APP_SITE ?? ""}/document?pdf=${doc.Id}:${encodeURIComponent(name)}:${doc.SignedUri}:${ItemKind.EditorDocument}:${doc.EncryptionKeyId ?? "null"}`,
      );
      return;
    }
    const kind = showsIncoming ? ItemKind.Parcel : ItemKind.OutgoingParcel;
    setPreview({ id: row.Id, kind, title: row.Title });
  };

  const gotoCase = (row: RegisterRow | undefined) => {
    if (!row?.CaseId) {
      notification({
        type: ENotificationType.INFO,
        title: "Brak teczki akt",
        text: "Pozycja nie jest powiązana z żadną teczką akt.",
      });
      return;
    }
    navigate(`/cases/${row.CaseId}/dossier`);
  };

  // ── Kolumny ─────────────────────────────────────────────────────────────
  const menuColumn: GridColDef = {
    field: "__menu",
    headerName: "",
    width: 44,
    sortable: false,
    resizable: false,
    hideable: false,
    disableColumnMenu: true,
    renderCell: (p: GridRenderCellParams<RegisterRow>) => (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setMenuAnchor({ el: e.currentTarget, row: p.row });
        }}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </IconButton>
    ),
  };

  const twoLine = (a?: string | null, b?: string | null) => (
    <div className={styles.twoLine}>
      <div className={styles.primary}>{a || "—"}</div>
      {b && <div className={styles.secondary}>{b}</div>}
    </div>
  );

  const incomingColumns: GridColDef[] = [
    { field: "ParcelNumber", headerName: "Nr", width: 120 },
    {
      field: "ReceiveDate", headerName: "Data wpływu", width: 110,
      valueGetter: (_: unknown, r: IIncomingRegisterItem) => r.ReceiveDate ?? r.CreationDate,
      valueFormatter: (v) => fmtDate(v as string),
    },
    {
      field: "SenderName", headerName: "Nadawca", flex: 1, minWidth: 200,
      renderCell: (p: GridRenderCellParams<IIncomingRegisterItem>) =>
        twoLine(p.row.SenderName, p.row.SenderAddress),
    },
    { field: "CategoryName", headerName: "Rodzaj", width: 150 },
    { field: "Title", headerName: "Tytuł", flex: 1, minWidth: 180 },
    { field: "DeliveryKindName", headerName: "Forma doręczenia", width: 150 },
    { field: "HolderName", headerName: "Prowadzący", width: 160 },
    { field: "CaseSignature", headerName: "Teczka", width: 140 },
    ...(mode === "archive"
      ? [{
          field: "__state", headerName: "Stan", width: 110,
          valueGetter: (_: unknown, r: IIncomingRegisterItem) =>
            r.IsRemoved ? "Usunięta" : r.IsHandled ? "Załatwiona" : "",
        } as GridColDef]
      : []),
    menuColumn,
  ];

  const outgoingColumns: GridColDef[] = [
    { field: "ParcelNumber", headerName: "Nr", width: 120 },
    {
      field: "SendDate", headerName: mode === "fees" ? "Data nadania" : "Data",
      width: 110,
      valueGetter: (_: unknown, r: IOutgoingRegisterItem) => r.SendDate ?? r.CreationDate,
      valueFormatter: (v) => fmtDate(v as string),
    },
    {
      field: "ReceiverName", headerName: "Adresat", flex: 1, minWidth: 200,
      renderCell: (p: GridRenderCellParams<IOutgoingRegisterItem>) =>
        twoLine(p.row.ReceiverName, p.row.ReceiverAddress),
    },
    { field: "Title", headerName: "Tytuł", flex: 1, minWidth: 180 },
    { field: "DeliveryKindName", headerName: "Forma doręczenia", width: 150 },
    ...(mode === "fees"
      ? [{
          field: "Fee", headerName: "Opłata", width: 110, align: "right", headerAlign: "right",
          valueFormatter: (v) => fmtPln(v as number),
        } as GridColDef]
      : [{ field: "DeliveryStatus", headerName: "Status", width: 130 } as GridColDef]),
    { field: "AuthorName", headerName: "Autor", width: 160 },
    { field: "DispatchChartSignature", headerName: "Karta nadawcza", width: 130 },
    { field: "DispatchNumber", headerName: "Nr nadawczy", width: 140 },
    { field: "CaseSignature", headerName: "Teczka", width: 140 },
    menuColumn,
  ];

  const internalColumns: GridColDef[] = [
    {
      field: "Date", headerName: "Data zatwierdzenia", width: 140,
      valueFormatter: (v) => fmtDate(v as string),
    },
    { field: "Title", headerName: "Tytuł", flex: 1, minWidth: 240 },
    { field: "AuthorName", headerName: "Autor", width: 180 },
    { field: "CaseSignature", headerName: "Teczka", width: 140 },
    { field: "VersionCount", headerName: "Wersje", width: 80, align: "center", headerAlign: "center" },
    menuColumn,
  ];

  const columns = showsIncoming ? incomingColumns : showsOutgoing ? outgoingColumns : internalColumns;

  // ── Wydruk listy (Take=0 → cały wynik bieżącego filtra) ─────────────────
  const [printing, setPrinting] = useState(false);

  const periodLabel = mode === "fees"
    ? `Rok: ${feeYears.length ? feeYears.join(", ") : "wszystkie"}${feeMonths.length ? "; miesiące: " + feeMonths.map((m) => MONTHS[m - 1]).join(", ") : ""}`
    : from || to ? `Okres: ${plDate(from)} – ${plDate(to)}` : "Cały okres";

  // BE ucina Take=0 do 5000 pozycji — ostrzeż, gdy wydruk nie obejmuje całego wyniku.
  const warnTruncated = (total: number, printed: number) => {
    if (total > printed)
      notification({
        type: ENotificationType.INFO,
        title: "Wydruk ucięty",
        text: `Wydrukowano ${printed} z ${total} pozycji — zawęź okres lub filtry.`,
      });
  };

  const doPrint = async (deliveryList = false) => {
    setPrinting(true);
    try {
      let ok = false;
      if (showsIncoming) {
        const { data } = await mailroomService.getIncomingRegister({ ...incomingRequest, Skip: 0, Take: 0 });
        warnTruncated(data.Total, data.Items.length);
        const cols: IPrintRegisterColumn<IIncomingRegisterItem>[] = deliveryList
          ? [
              { header: "Nr", value: (r) => r.ParcelNumber, width: "10%" },
              { header: "Nazwa jednostki organizacyjnej lub nazwisko interesanta", value: (r) => [r.SenderName, r.SenderAddress].filter(Boolean).join(", "), width: "34%" },
              { header: "Data wpływu", value: (r) => fmtDate(r.ReceiveDate ?? r.CreationDate), width: "12%", align: "center" },
              { header: "Określenie sprawy", value: (r) => [r.CategoryName, r.Title].filter(Boolean).join(" — "), width: "26%" },
            ]
          : [
              { header: "Lp.", value: (_, i) => i + 1, width: "5%", align: "center" },
              { header: "Nr", value: (r) => r.ParcelNumber, width: "9%" },
              { header: "Data wpływu", value: (r) => fmtDate(r.ReceiveDate ?? r.CreationDate), width: "9%", align: "center" },
              { header: "Nadawca", value: (r) => [r.SenderName, r.SenderAddress].filter(Boolean).join(", "), width: "22%" },
              { header: "Rodzaj", value: (r) => r.CategoryName, width: "11%" },
              { header: "Tytuł", value: (r) => r.Title, width: "18%" },
              { header: "Forma doręczenia", value: (r) => r.DeliveryKindName, width: "10%" },
              { header: "Prowadzący", value: (r) => r.HolderName, width: "9%" },
              { header: "Teczka", value: (r) => r.CaseSignature, width: "7%" },
            ];
        ok = printRegister<IIncomingRegisterItem>({
          title: deliveryList ? "Lista doręczeń przesyłek przychodzących" : MAILROOM_REGISTER_TITLES[mode],
          subtitle: periodLabel,
          columns: cols,
          rows: data.Items,
          signatureColumn: deliveryList,
        });
      } else if (showsOutgoing) {
        const { data } = await mailroomService.getOutgoingRegister({ ...outgoingRequest, Skip: 0, Take: 0 });
        warnTruncated(data.Total, data.Items.length);
        const cols: IPrintRegisterColumn<IOutgoingRegisterItem>[] = [
          { header: "Lp.", value: (_, i) => i + 1, width: "5%", align: "center" },
          { header: "Nr", value: (r) => r.ParcelNumber, width: "9%" },
          { header: mode === "fees" ? "Data nadania" : "Data", value: (r) => fmtDate(r.SendDate ?? r.CreationDate), width: "9%", align: "center" },
          { header: "Adresat", value: (r) => [r.ReceiverName, r.ReceiverAddress].filter(Boolean).join(", "), width: "22%" },
          { header: "Tytuł", value: (r) => r.Title, width: "18%" },
          { header: "Forma doręczenia", value: (r) => r.DeliveryKindName, width: "10%" },
          mode === "fees"
            ? { header: "Opłata", value: (r) => fmtPln(r.Fee), width: "9%", align: "right" }
            : { header: "Status", value: (r) => r.DeliveryStatus, width: "9%" },
          { header: "Autor", value: (r) => r.AuthorName, width: "9%" },
          { header: "Karta / nr nadawczy", value: (r) => [r.DispatchChartSignature, r.DispatchNumber].filter(Boolean).join(" / "), width: "9%" },
        ];
        ok = printRegister<IOutgoingRegisterItem>({
          title: MAILROOM_REGISTER_TITLES[mode],
          subtitle: periodLabel,
          columns: cols,
          rows: data.Items,
          footer: mode === "fees"
            ? [
                `Suma opłat: ${fmtPln(data.TotalFee)}`,
                ...data.FeeByKind.map((k) => `${k.DeliveryKindName}: ${k.Count} szt. — ${fmtPln(k.Fee)}`),
              ]
            : undefined,
        });
      } else {
        const { data } = await mailroomService.getInternalRegister({ ...internalRequest, Skip: 0, Take: 0 });
        warnTruncated(data.Total, data.Items.length);
        ok = printRegister<IInternalRegisterItem>({
          title: MAILROOM_REGISTER_TITLES[mode],
          subtitle: periodLabel,
          columns: [
            { header: "Lp.", value: (_, i) => i + 1, width: "5%", align: "center" },
            { header: "Data zatwierdzenia", value: (r) => fmtDate(r.Date), width: "12%", align: "center" },
            { header: "Tytuł", value: (r) => r.Title, width: "45%" },
            { header: "Autor", value: (r) => r.AuthorName, width: "20%" },
            { header: "Teczka", value: (r) => r.CaseSignature, width: "18%" },
          ],
          rows: data.Items,
        });
      }
      if (!ok) {
        notification({
          type: ENotificationType.ERROR,
          title: "Nie udało się otworzyć wydruku",
          text: "Zezwól na wyskakujące okna dla tej strony.",
        });
      }
    } catch {
      notification({
        type: ENotificationType.ERROR,
        title: "Wydruk listy",
        text: "Nie udało się pobrać pozycji rejestru.",
      });
    } finally {
      setPrinting(false);
    }
  };

  const openPopup = (e: React.MouseEvent, setter: (p: PopupPos) => void, current: PopupPos) => {
    if (current) { setter(null); return; }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setter({ top: rect.bottom + 4, left: rect.left });
  };

  const showKindFilter = mode !== "internal";
  const showTypeFilter = showsIncoming;

  return (
    <div className={styles.wrapper}>
      {/* ── Pasek filtrów ── */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          {mode === "archive" && (
            <div className={styles.segmented}>
              <button
                className={direction === "incoming" ? styles.segActive : ""}
                onClick={() => setDirection("incoming")}
              >
                Przychodzące
              </button>
              <button
                className={direction === "outgoing" ? styles.segActive : ""}
                onClick={() => setDirection("outgoing")}
              >
                Wychodzące
              </button>
            </div>
          )}

          {usesPeriod ? (
            <div
              className={styles.filterBtn}
              onClick={(e) => openPopup(e, setPeriodPopupPos, periodPopupPos)}
            >
              {from || to ? `${plDate(from)} – ${plDate(to)}` : "Okres"}
              <FontAwesomeIcon icon={periodPopupPos ? faChevronUp : faChevronDown} />
            </div>
          ) : (
            <>
              <FormControl size="small" className={styles.select}>
                <InputLabel>Rok</InputLabel>
                <Select
                  multiple
                  label="Rok"
                  value={feeYears}
                  onChange={(e) => setFeeYears(e.target.value as number[])}
                  renderValue={(v) => (v as number[]).join(", ") || "wszystkie"}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <MenuItem key={y} value={y}>
                      <Checkbox size="small" checked={feeYears.includes(y)} />
                      <ListItemText primary={y} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" className={styles.selectWide}>
                <InputLabel>Miesiąc</InputLabel>
                <Select
                  multiple
                  label="Miesiąc"
                  value={feeMonths}
                  onChange={(e) => setFeeMonths((e.target.value as number[]).sort((a, b) => a - b))}
                  renderValue={(v) =>
                    (v as number[]).length ? (v as number[]).map((m) => MONTHS[m - 1]).join(", ") : "wszystkie"
                  }
                >
                  {MONTHS.map((name, i) => (
                    <MenuItem key={name} value={i + 1}>
                      <Checkbox size="small" checked={feeMonths.includes(i + 1)} />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {showKindFilter && (
            <FormControl size="small" className={styles.selectWide}>
              <InputLabel>Forma doręczenia</InputLabel>
              <Select
                label="Forma doręczenia"
                value={deliveryKindId}
                onChange={(e) => { const v = e.target.value as number | ""; setDeliveryKindId(v === "" ? "" : Number(v)); }}
              >
                <MenuItem value="">wszystkie</MenuItem>
                {deliveryKinds.map((k) => (
                  <MenuItem key={k.Id} value={k.Id}>{k.Name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {showTypeFilter && (
            <FormControl size="small" className={styles.selectWide}>
              <InputLabel>Rodzaj</InputLabel>
              <Select
                label="Rodzaj"
                value={attachmentTypeId}
                onChange={(e) => { const v = e.target.value as number | ""; setAttachmentTypeId(v === "" ? "" : Number(v)); }}
              >
                <MenuItem value="">wszystkie</MenuItem>
                {attachmentTypes.map((t) => (
                  <MenuItem key={t.Id} value={t.Id}>{t.Name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>

        <div className={styles.toolbarRight}>
          {mode === "fees" && (
            <div className={styles.feeSummary} title={feeByKind.map((k) => `${k.DeliveryKindName}: ${k.Count} szt. — ${fmtPln(k.Fee)}`).join("\n")}>
              Suma opłat: <strong>{fmtPln(totalFee)}</strong>
            </div>
          )}
          <div className={styles.count}>Pozycji: {total}</div>
          {showsIncoming && (
            <Tooltip title={"Lista doręczeń z miejscem na podpis (odpowiednik „Drukuj listę doręczeń” z Magistratu)"}>
              <button className={styles.printBtn} disabled={printing} onClick={() => doPrint(true)}>
                <FontAwesomeIcon icon={faPrint} /> Lista doręczeń
              </button>
            </Tooltip>
          )}
          <Tooltip title="Wydruk listy wg bieżącego filtra">
            <button className={styles.printBtn} disabled={printing} onClick={() => doPrint(false)}>
              <FontAwesomeIcon icon={faPrint} /> Drukuj listę
            </button>
          </Tooltip>
        </div>
      </div>

      {/* ── Lista ── */}
      <div className={styles.list}>
        <DataGridPremium
          localeText={plGridLocales}
          slots={{ noRowsOverlay: CustomNoRowsOverlay }}
          slotProps={{ loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" } }}
          loading={isLoading}
          columnHeaderHeight={30}
          rowHeight={44}
          columns={columns}
          rows={rows}
          rowCount={total}
          paginationMode="server"
          pagination
          paginationModel={{ page, pageSize: PAGE_SIZE }}
          pageSizeOptions={[PAGE_SIZE]}
          onPaginationModelChange={(m) => setPage(m.page)}
          disableColumnFilter
          disablePivoting
          disableAggregation
          disableRowGrouping
          disableRowSelectionOnClick
          rowSelectionModel={selectedRowIds}
          onRowClick={handleRowClick}
          onRowDoubleClick={(p) => openPreview(p.row as RegisterRow)}
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": { outline: "none !important" },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus": { outline: "none !important" },
          }}
        />
      </div>

      {/* ── Menu wiersza ── */}
      <Menu anchorEl={menuAnchor?.el ?? null} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { const r = menuAnchor?.row; setMenuAnchor(null); openPreview(r); }}>
          Podgląd
        </MenuItem>
        <MenuItem
          disabled={!menuAnchor?.row?.CaseId}
          onClick={() => { const r = menuAnchor?.row; setMenuAnchor(null); gotoCase(r); }}
        >
          Idź do teczki
        </MenuItem>
      </Menu>

      {/* ── Podgląd ── */}
      {preview && preview.kind === ItemKind.OutgoingParcel && (
        <ParcelPreviewModal parcelId={preview.id} title={preview.title} onClose={() => setPreview(null)} />
      )}
      {preview && preview.kind !== ItemKind.OutgoingParcel && (
        <ShowPackageModal
          previewPackage={{ Id: preview.id, Kind: preview.kind } as IPackage}
          toggle={() => setPreview(null)}
        />
      )}

      {/* ── Popup: Okres ── */}
      {periodPopupPos && createPortal(
        <>
          <div className={styles.popupBackdrop} onClick={() => setPeriodPopupPos(null)} />
          <div
            className={styles.periodDropdown}
            style={{ position: "fixed", top: periodPopupPos.top, left: periodPopupPos.left, zIndex: 1400 }}
          >
            <PeriodPicker
              year={year}
              from={from}
              to={to}
              preset={preset}
              yearOptions={YEAR_OPTIONS}
              setYear={setYear}
              setFrom={setFrom}
              setTo={setTo}
              setPreset={setPreset}
            />
            <button
              className={styles.clearPeriod}
              onClick={() => { setFrom(""); setTo(""); setPreset(null); }}
            >
              Cały okres (bez ograniczenia)
            </button>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};

export default MailroomRegister;
